import { Quat, Vec3 } from 'playcanvas';

import type { KeyEasing } from './anim-track';
import { ElementType } from './element';
import { Events } from './events';
import { HelperPoint } from './helper-point';
import { Scene } from './scene';
import { SceneCameraAnimTrack, type SceneCameraPose } from './scene-camera-anim-track';

const VALID_EASINGS = new Set(['linear', 'easeIn', 'easeOut', 'easeInOut', 'step']);

type DocVec3 = [number, number, number];
type DocQuat = [number, number, number, number];

type SceneCameraDocBundleV0 = {
    version: 0;
    name: string;
    visible: boolean;
    position: DocVec3;
    rotation: DocQuat;
    scale: DocVec3;
    fov: number;
    aspect: number;
    poses: Array<{
        name: string;
        frame: number;
        position: DocVec3;
        target: DocVec3;
        easing?: string;
    }>;
};

const pack3 = (v: Vec3): DocVec3 => [v.x, v.y, v.z];
const pack4 = (q: Quat): DocQuat => [q.x, q.y, q.z, q.w];

const hasVec3Tuple = (v: unknown): v is DocVec3 => {
    return Array.isArray(v) && v.length === 3 &&
        Number.isFinite(v[0]) && Number.isFinite(v[1]) && Number.isFinite(v[2]);
};

const hasQuatTuple = (v: unknown): v is DocQuat => {
    return Array.isArray(v) && v.length === 4 &&
        Number.isFinite(v[0]) && Number.isFinite(v[1]) &&
        Number.isFinite(v[2]) && Number.isFinite(v[3]);
};

const isDocBundleV0 = (v: any): v is SceneCameraDocBundleV0 => {
    return (
        v && typeof v === 'object' &&
        v.version === 0 &&
        typeof v.name === 'string' &&
        typeof v.visible === 'boolean' &&
        hasVec3Tuple(v.position) &&
        hasQuatTuple(v.rotation) &&
        hasVec3Tuple(v.scale) &&
        Array.isArray(v.poses)
    );
};

const isPoseShape = (v: any): boolean => {
    return (
        v && typeof v === 'object' &&
        typeof v.name === 'string' &&
        typeof v.frame === 'number' && Number.isFinite(v.frame) &&
        hasVec3Tuple(v.position) &&
        hasVec3Tuple(v.target)
    );
};

export const registerSceneCameraDocEvents = (scene: Scene, events: Events) => {
    events.function('docSerialize.sceneCameras', (): SceneCameraDocBundleV0[] => {
        const helpers = scene.getElementsByType(ElementType.helper) as HelperPoint[];
        const bundles: SceneCameraDocBundleV0[] = [];

        for (const helper of helpers) {
            const data = (helper as any).sceneCameraData;
            if (!data) continue;

            const animTrack = data.animTrack as SceneCameraAnimTrack;
            const poses = animTrack.getPoses();

            bundles.push({
                version: 0,
                name: helper.name,
                visible: !!helper.visible,
                position: pack3(helper.entity.getLocalPosition()),
                rotation: pack4(helper.entity.getLocalRotation()),
                scale: pack3(helper.entity.getLocalScale()),
                fov: data.fov ?? 60,
                aspect: data.aspect ?? 16 / 9,
                poses: poses.map(p => ({
                    name: p.name,
                    frame: p.frame,
                    position: pack3(p.position),
                    target: pack3(p.target),
                    easing: p.easing
                }))
            });
        }

        return bundles;
    });

    events.function('docDeserialize.sceneCameras', async (data: any) => {
        if (!Array.isArray(data) || data.length < 1) return;

        for (const raw of data) {
            if (!isDocBundleV0(raw)) continue;

            const helper = new HelperPoint({
                name: raw.name,
                animated: false,
                visible: raw.visible
            });

            helper.entity.setLocalPosition(new Vec3(raw.position[0], raw.position[1], raw.position[2]));
            helper.entity.setLocalRotation(new Quat(raw.rotation[0], raw.rotation[1], raw.rotation[2], raw.rotation[3]));
            helper.entity.setLocalScale(new Vec3(raw.scale[0], raw.scale[1], raw.scale[2]));

            await scene.add(helper);

            const animTrack = new SceneCameraAnimTrack(events, helper.entity);

            // Load validated poses
            const validPoses: SceneCameraPose[] = [];
            for (const p of raw.poses) {
                if (!isPoseShape(p)) continue;
                validPoses.push({
                    name: p.name,
                    frame: p.frame,
                    position: new Vec3(p.position[0], p.position[1], p.position[2]),
                    target: new Vec3(p.target[0], p.target[1], p.target[2]),
                    easing: (typeof p.easing === 'string' && VALID_EASINGS.has(p.easing)) ? p.easing as KeyEasing : undefined
                });
            }
            if (validPoses.length > 0) {
                animTrack.loadPoses(validPoses);
            }

            (helper as any).sceneCameraData = {
                animTrack,
                lookThrough: false,
                fov: (typeof raw.fov === 'number' && Number.isFinite(raw.fov)) ? raw.fov : 60,
                aspect: (typeof raw.aspect === 'number' && Number.isFinite(raw.aspect)) ? raw.aspect : 16 / 9,
                _timeHandle: null,
                _frameHandle: null
            };
        }

        events.fire('hierarchy.changed');
    });
};
